/**
 * Geografía administrativa de Colombia (DANE/DIVIPOLA).
 *
 * - `COLOMBIA_DEPARTMENTS`: los 32 departamentos + Bogotá D.C. con código 2 dígitos.
 * - `COLOMBIA_MUNICIPALITIES`: subset curado (~250) de los municipios más
 *   poblados por departamento. NO es la lista DIVIPOLA completa (~1.122);
 *   cubre la mayoría de casos reales. Si un tenant está en un municipio
 *   fuera de esta lista, la UI permite texto libre como fallback.
 *
 * Se usa en la admin web para configurar la ubicación por defecto del
 * punto de venta, que el POS aprovecha como valor inicial del campo
 * "ciudad" en pedidos con domicilio.
 */
export const COLOMBIA_DEPARTMENTS = [
    { code: '05', name: 'Antioquia' },
    { code: '08', name: 'Atlántico' },
    { code: '11', name: 'Bogotá D.C.' },
    { code: '13', name: 'Bolívar' },
    { code: '15', name: 'Boyacá' },
    { code: '17', name: 'Caldas' },
    { code: '18', name: 'Caquetá' },
    { code: '19', name: 'Cauca' },
    { code: '20', name: 'Cesar' },
    { code: '23', name: 'Córdoba' },
    { code: '25', name: 'Cundinamarca' },
    { code: '27', name: 'Chocó' },
    { code: '41', name: 'Huila' },
    { code: '44', name: 'La Guajira' },
    { code: '47', name: 'Magdalena' },
    { code: '50', name: 'Meta' },
    { code: '52', name: 'Nariño' },
    { code: '54', name: 'Norte de Santander' },
    { code: '63', name: 'Quindío' },
    { code: '66', name: 'Risaralda' },
    { code: '68', name: 'Santander' },
    { code: '70', name: 'Sucre' },
    { code: '73', name: 'Tolima' },
    { code: '76', name: 'Valle del Cauca' },
    { code: '81', name: 'Arauca' },
    { code: '85', name: 'Casanare' },
    { code: '86', name: 'Putumayo' },
    { code: '88', name: 'San Andrés y Providencia' },
    { code: '91', name: 'Amazonas' },
    { code: '94', name: 'Guainía' },
    { code: '95', name: 'Guaviare' },
    { code: '97', name: 'Vaupés' },
    { code: '99', name: 'Vichada' },
];
export const isColombiaDepartment = (name) => COLOMBIA_DEPARTMENTS.some((d) => d.name === name);
/**
 * Mapa interno compacto: departamento → municipios ordenados (capital primero).
 * Se exporta aplanado como `COLOMBIA_MUNICIPALITIES` para iteración simple.
 */
const BY_DEPARTMENT = {
    Antioquia: [
        'Medellín',
        'Bello',
        'Itagüí',
        'Envigado',
        'Apartadó',
        'Turbo',
        'Rionegro',
        'Sabaneta',
        'La Estrella',
        'Copacabana',
        'Caldas',
        'Girardota',
        'Marinilla',
        'Caucasia',
        'El Bagre',
        'Yarumal',
        'Puerto Berrío',
        'Andes',
        'Santa Rosa de Osos',
        'La Ceja',
        'Amagá',
        'Carepa',
        'Chigorodó',
    ],
    Atlántico: [
        'Barranquilla',
        'Soledad',
        'Malambo',
        'Puerto Colombia',
        'Sabanagrande',
        'Galapa',
        'Baranoa',
        'Sabanalarga',
        'Palmar de Varela',
        'Santo Tomás',
    ],
    'Bogotá D.C.': ['Bogotá'],
    Bolívar: [
        'Cartagena',
        'Magangué',
        'Turbaco',
        'El Carmen de Bolívar',
        'Arjona',
        'San Jacinto',
        'Mompós',
        'María la Baja',
        'San Pablo',
        'Mahates',
    ],
    Boyacá: [
        'Tunja',
        'Duitama',
        'Sogamoso',
        'Chiquinquirá',
        'Paipa',
        'Moniquirá',
        'Villa de Leyva',
        'Samacá',
        'Nobsa',
        'Garagoa',
    ],
    Caldas: [
        'Manizales',
        'La Dorada',
        'Chinchiná',
        'Villamaría',
        'Riosucio',
        'Anserma',
        'Supía',
        'Salamina',
        'Aguadas',
    ],
    Caquetá: [
        'Florencia',
        'San Vicente del Caguán',
        'Puerto Rico',
        'La Montañita',
        'El Doncello',
        'Cartagena del Chairá',
    ],
    Cauca: [
        'Popayán',
        'Santander de Quilichao',
        'Puerto Tejada',
        'Guapi',
        'Patía (El Bordo)',
        'Caloto',
        'Timbío',
        'Piendamó',
    ],
    Cesar: [
        'Valledupar',
        'Aguachica',
        'Agustín Codazzi',
        'La Jagua de Ibirico',
        'Bosconia',
        'Chiriguaná',
        'Curumaní',
        'Pailitas',
    ],
    Córdoba: [
        'Montería',
        'Ayapel',
        'Buenavista',
        'Canalete',
        'Cereté',
        'Chimá',
        'Chinú',
        'Ciénaga de Oro',
        'Cotorra',
        'La Apartada',
        'Lorica',
        'Los Córdobas',
        'Momil',
        'Montelíbano',
        'Moñitos',
        'Planeta Rica',
        'Pueblo Nuevo',
        'Puerto Escondido',
        'Puerto Libertador',
        'Purísima de la Concepción',
        'Sahagún',
        'San Andrés de Sotavento',
        'San Antero',
        'San Bernardo del Viento',
        'San Carlos',
        'San José de Uré',
        'San Pelayo',
        'Tierralta',
        'Tuchín',
        'Valencia',
    ],
    Cundinamarca: [
        'Soacha',
        'Zipaquirá',
        'Facatativá',
        'Chía',
        'Mosquera',
        'Fusagasugá',
        'Madrid',
        'Funza',
        'Cajicá',
        'Girardot',
        'La Calera',
        'Cota',
        'Tocancipá',
        'Sopó',
        'Ubaté',
        'Fómeque',
        'Tenjo',
        'Pacho',
        'Zipacón',
        'Anapoima',
    ],
    Chocó: [
        'Quibdó',
        'Istmina',
        'Tadó',
        'Condoto',
        'Acandí',
        'Riosucio',
        'Bahía Solano',
        'Nuquí',
    ],
    Huila: [
        'Neiva',
        'Pitalito',
        'Garzón',
        'La Plata',
        'Campoalegre',
        'Gigante',
        'Isnos',
        'San Agustín',
        'Aipe',
    ],
    'La Guajira': [
        'Riohacha',
        'Maicao',
        'Uribia',
        'Manaure',
        'San Juan del Cesar',
        'Fonseca',
        'Dibulla',
        'Albania',
        'Villanueva',
    ],
    Magdalena: [
        'Santa Marta',
        'Ciénaga',
        'Fundación',
        'El Banco',
        'Plato',
        'Aracataca',
        'Pivijay',
        'Zona Bananera',
        'Sitionuevo',
    ],
    Meta: [
        'Villavicencio',
        'Acacías',
        'Puerto López',
        'Granada',
        'Cumaral',
        'Restrepo',
        'San Martín',
        'Puerto Gaitán',
        'Guamal',
    ],
    Nariño: [
        'Pasto',
        'Tumaco',
        'Ipiales',
        'Túquerres',
        'La Unión',
        'Samaniego',
        'Sandoná',
        'Taminango',
    ],
    'Norte de Santander': [
        'Cúcuta',
        'Ocaña',
        'Pamplona',
        'Villa del Rosario',
        'Los Patios',
        'Tibú',
        'Chinácota',
        'El Zulia',
    ],
    Quindío: [
        'Armenia',
        'Calarcá',
        'La Tebaida',
        'Montenegro',
        'Quimbaya',
        'Circasia',
        'Filandia',
        'Salento',
    ],
    Risaralda: [
        'Pereira',
        'Dosquebradas',
        'Santa Rosa de Cabal',
        'La Virginia',
        'Belén de Umbría',
        'Quinchía',
        'Marsella',
    ],
    Santander: [
        'Bucaramanga',
        'Floridablanca',
        'Girón',
        'Piedecuesta',
        'Barrancabermeja',
        'San Gil',
        'Socorro',
        'Málaga',
        'Lebrija',
        'Rionegro',
        'Sabana de Torres',
        'Zapatoca',
        'Vélez',
    ],
    Sucre: [
        'Sincelejo',
        'Corozal',
        'San Marcos',
        'Sampués',
        'Sincé',
        'Tolú',
        'San Onofre',
        'Coveñas',
    ],
    Tolima: [
        'Ibagué',
        'Espinal',
        'Melgar',
        'Honda',
        'Mariquita',
        'Líbano',
        'Chaparral',
        'Flandes',
        'Purificación',
        'Guamo',
    ],
    'Valle del Cauca': [
        'Cali',
        'Palmira',
        'Buenaventura',
        'Tuluá',
        'Cartago',
        'Buga',
        'Yumbo',
        'Jamundí',
        'Candelaria',
        'Florida',
        'Pradera',
        'Zarzal',
        'Roldanillo',
        'Sevilla',
        'Dagua',
        'La Unión',
    ],
    Arauca: ['Arauca', 'Saravena', 'Tame', 'Fortul', 'Arauquita'],
    Casanare: [
        'Yopal',
        'Aguazul',
        'Tauramena',
        'Villanueva',
        'Paz de Ariporo',
        'Monterrey',
        'Orocué',
    ],
    Putumayo: [
        'Mocoa',
        'Puerto Asís',
        'Orito',
        'Valle del Guamuez',
        'Villagarzón',
        'Sibundoy',
    ],
    'San Andrés y Providencia': ['San Andrés', 'Providencia'],
    Amazonas: ['Leticia', 'Puerto Nariño'],
    Guainía: ['Inírida'],
    Guaviare: ['San José del Guaviare', 'El Retorno', 'Calamar'],
    Vaupés: ['Mitú', 'Carurú'],
    Vichada: ['Puerto Carreño', 'La Primavera', 'Cumaribo'],
};
export const COLOMBIA_MUNICIPALITIES = Object.entries(BY_DEPARTMENT).flatMap(([department, munis]) => munis.map((name) => ({ name, department })));
/**
 * Devuelve los municipios del departamento dado, ordenados por la
 * posición original (capital primero, demás por tamaño/relevancia).
 * Retorna array vacío si el departamento no está en el catálogo.
 */
export const getMunicipalitiesByDepartment = (department) => BY_DEPARTMENT[department] ?? [];
//# sourceMappingURL=colombia.js.map